import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import sidenavConfig from "../../../Sidenav/config";
import SidenavMenu from "../../../Sidenav/components/Menu";
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';
import { NOTIFICATION_MODE, useForms } from "../../../../context/Forms";
import { renderModule } from "../../../Sidenav";
import { Style } from "./style";
import { actions } from "../../../../store/reducers/authentication";
import { isSuperAdmin } from "../../../../constants/user";
import { noDatastakeApps, withHiddenApps } from "../../../../constants/apps";
import { usePermissions } from "../../../../permissions/usePermissions";
import { useApp } from "../../../../helpers/user";

const isCollapsed = false;

const isDev = ['dev', 'development', 'local', 'demo'].includes(process.env.REACT_APP_ENV);
const selectedKeys = [];

export default function MobileDrawer({
  mod = 'pme',
  toggle = () => { },
  drawerOpened = false,
  isUserDropdown = false,
}) {
  const { t } = useTranslation();
  const app = useApp();
  const push = useNavigate();
  const { notificationMode, changeNotificationState } = useForms();
  const { user, selectedProject, selectedProject: project } = useSelector((state) => state.authentication);
  const items = useMemo(() => sidenavConfig[mod] || [], [mod]);
  const dispatch = useDispatch();
  const canViewPartners = useMemo(() => mod === 'tazama', [mod]);
  const canViewProjects = usePermissions({ permission: 'projects.canView', defaultValue: false });
  const canViewUsers = usePermissions({ permission: 'users.canView', defaultValue: false });
  const canViewSettings = usePermissions({ permission: 'settings.canView', defaultValue: false });
  const hasMultipleApps = useMemo(() =>
    Object.keys(user?.modules || {}).filter((k) => user.modules[k].status === 'approved').length > 1, [user])

  const logOut = useCallback(() => {
    const hasPrevious = (localStorage.getItem('previous'));
    sessionStorage.removeItem('notifications');
    if (hasPrevious) {
      dispatch(actions.exitImpersonation());
      return;
    }

    dispatch(actions.logout());
  }, [dispatch]);

  const goTo = (...props) => {
    push(...props);
    toggle();
  }

  const isDemo = process.env.REACT_APP_ENV === 'demo' && !isSuperAdmin(user);

  const filteredItems = useMemo(() => {
    const mapItems = (item) => {
      const isDisabled = typeof item.isDisabled === 'function' ?
        item.isDisabled(user, project, selectedProject)
        : item.isDisabled || false;

      if (item.items) {
        return {
          ...item, isDisabled, items: item.items.filter((item) => {
            const res = typeof item.visible === 'function' ? item.visible(user, project, selectedProject) : true;
            return res;
          }).map(mapItems)
        };
      }

      return { ...item, isDisabled };
    };

    if (isUserDropdown) {
      return [
        { type: 'link', name: t('Projects'), path: `/app/${mod}/projects`, isDashboard: true, visible: canViewProjects },
        { type: 'link', name: t('Partners'), path: `/app/${mod}/partners`, isDashboard: true, visible: canViewPartners },
        { type: 'link', name: t('Users'), path: `/app/${mod}/users`, isDashboard: true, visible: canViewUsers },
        { type: 'link', name: t('Settings'), path: `/app/${mod}/view/settings`, isDashboard: true, visible: canViewSettings },
        { type: 'link', name: t('Applications'), path: `/app`, isDashboard: true, visible: !withHiddenApps.includes(app), isDisabled: !hasMultipleApps },
        { type: 'link', name: t('Log out'), onClick: () => logOut(), isDashboard: true, visible: true },
      ]
        .filter((v) => v.visible)
        .map(mapItems);
    }

    return items
      .filter(item => {
        const res = item.visible(user, project, selectedProject);
        return res;
      })
      .map(mapItems);
  }, [items, user, project, selectedProject, t, isUserDropdown, mod, logOut, hasMultipleApps, canViewPartners, canViewUsers, canViewSettings, canViewProjects, app]);

  const checkOnClick = ({ event }) => {

    if (notificationMode === NOTIFICATION_MODE.PROJECT_SOURCES) {
      changeNotificationState({
        onYes: () => {
          event();
        }
      });
    } else {
      event();
    }
  }

  return (
    <Style className={formatClassname([!drawerOpened && 'closed', isUserDropdown && 'user-dropdown'])}>
      <div className="drawer">
        <div
          className={formatClassname([isCollapsed ? 'sidenav-sider-collapsed sidenav-sider flex-1' : 'sidenav-sider sidenav-sider-opened flex-1', app])}
          style={{ width: isCollapsed ? '70px' : '250px', minWidth: isCollapsed ? 'auto' : '250px' }}>
          <div className="flex">
            <div className="flex-1">
              {mod === 'app' ? null : renderModule({
                isCollapsed,
                mod: mod,
                module: mod,
              })}
            </div>
            <div className="cursor-pointer close-icon" onClick={toggle}>
            </div>
          </div>
          <div className="sidenav-cont">
            <div className={formatClassname(['drawer', 'menus-cont', 'not-collapsed'])}>
              <SidenavMenu
                module={mod}
                menuMode="inline"
                selectedKeys={selectedKeys}
                filteredItems={filteredItems}
                isCollapsed={false}
                setHoverItem={() => { }}
                setTopHover={() => { }}
                setHoverOpen={() => { }}
                onMouseMove={() => { }}
                user={user}
                goTo={goTo}
                isDemo={isDemo}
                isDev={isDev}
                showSettings
                t={t}
                checkOnClick={checkOnClick}
                project={project}
              />
            </div>

            <div className="sidenav-footer">
              {!noDatastakeApps.includes(mod) ? (
                <>
                  <h6>{t('Powered By')}</h6>
                  <img src="/assets/images/datastake-white.svg" alt="logo" />
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Style>
  )
}
